import React, { FC, memo, useCallback, useState } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Button } from 'shared/ui/Button';
import { ReactComponent as ProfileIcon } from 'shared/assets/icons/newIcons/ProfileIcon.svg';
import { ReactComponent as SendEmailIcon } from 'shared/assets/icons/newIcons/SendEmailIcon.svg';
import { ReactComponent as CallIcon } from 'shared/assets/icons/newIcons/CallIcon.svg';
import { ReactComponent as PassportIcon } from 'shared/assets/icons/newIcons/PassportIcon.svg';
import { ReactComponent as PaidDaysIcon } from 'shared/assets/icons/newIcons/PaidDaysIcon.svg';
import { UserAccountType, ProfileVerificationStatus, useRole } from 'entities/User';
import { NewPasswordModal } from './NewPasswordModal';
import { AccountVerificationNotice } from './AccountVerificationNotice';
import { UploadDocumentsModal } from './UploadDocumentsModal';
import { VerificationStatusIndicator } from 'features/VerificationStatusIndicator';
import { IdDetailsDrawer } from './IdDetailsDrawer';
import { getClientDateFormat } from 'shared/utils/helpers/getDateFormat';

interface AccountDetailsFormProps {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  accountType: UserAccountType;
  company?: Nullable<string>;
  identificationNumber?: Nullable<string>;
  loggedUserId?: string;
  profileVerificationStatus: ProfileVerificationStatus | null;
  dateOfBirth?: Nullable<string>;
  identificationDocIssuingAuthority?: Nullable<string>;
  identificationDocIssuingDate?: Nullable<string>;
  residenceRegistration?: Nullable<string>;
  signingIdentificator?: Nullable<string>;
  middleName?: Nullable<string>;
}

export const AccountDetailsForm: FC<AccountDetailsFormProps> = memo((props) => {
  const {
    userId,
    firstName,
    lastName,
    email,
    phone,
    accountType,
    company,
    identificationNumber,
    profileVerificationStatus,
    loggedUserId,
    identificationDocIssuingAuthority,
    dateOfBirth,
    identificationDocIssuingDate,
    residenceRegistration,
    signingIdentificator,
    middleName,
  } = props;

  const { t } = useAppTranslation(['profile', 'booking']);

  const [newPasswordModalOpened, setNewPasswordModalOpened] = useState<boolean>(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState<boolean>(false);
  const [isIdDetailsDrawerOpen, setIsIdDetailsDrawerOpen] = useState<boolean>(false);

  const { isManagement } = useRole();

  const openIdDetailsDrawer = useCallback(() => {
    setIsIdDetailsDrawerOpen(true);
  }, []);

  const closeIdDetailsDrawer = useCallback(() => {
    setIsIdDetailsDrawerOpen(false);
  }, []);

  const openUploadModal = useCallback(() => {
    setIsUploadModalOpen(true);
  }, []);

  const closeUploadModal = useCallback(() => {
    setIsUploadModalOpen(false);
  }, []);

  const openNewPasswordModal = useCallback(() => {
    setNewPasswordModalOpened(true);
  }, []);

  const closeNewPasswordModal = useCallback(() => {
    setNewPasswordModalOpened(false);
  }, []);

  return (
    <div className="flex flex-col p-4 bg-white rounded-lg gap-4">
      <div className="flex justify-between items-center">
        <div className="text-2xl flex font-semibold font-tektur items-center">
          {t('Account details')}
          <VerificationStatusIndicator status={profileVerificationStatus} />
        </div>
        <div className="text-accent">{t(accountType, { ns: 'booking' })}</div>
      </div>

      <AccountVerificationNotice userId={userId} onUploadClick={openUploadModal} />

      <div className="flex gap-2 items-center text-primaryLight">
        <ProfileIcon className="w-[28px] h-[28px] shrink-0" />
        <div className="text-primaryLight text-lg gap-2 font-tektur">
          {firstName || '-'} {middleName || ''} {lastName || ''}
        </div>
      </div>
      {accountType === UserAccountType.BUSINESS && (
        <div className="flex gap-2 items-center text-primaryLight">
          <PaidDaysIcon className="w-[28px] h-[28px] shrink-0" />
          <div className="text-primaryLight text-lg font-tektur">{company || '-'}</div>
        </div>
      )}
      <div className="flex gap-2 items-center text-primaryLight">
        <SendEmailIcon className="w-[28px] h-[28px] shrink-0" />
        <div className="text-primaryLight text-lg font-tektur">{email || '-'}</div>
      </div>
      <div className="flex gap-2 items-center text-primaryLight">
        <CallIcon className="w-[28px] h-[28px] shrink-0" />
        <div className="text-primaryLight text-lg font-tektur">{phone || '-'}</div>
      </div>
      <div className="flex gap-2 items-center text-primaryLight">
        <PassportIcon className="w-[28px] h-[28px] shrink-0" />
        <div className="text-primaryLight text-lg font-tektur">{identificationNumber || '-'}</div>
      </div>
      {identificationDocIssuingAuthority && (
        <div className="flex gap-2 items-center text-primaryLight">
          <PassportIcon className="w-[28px] h-[28px] shrink-0" />
          <div className="text-primaryLight text-lg font-tektur">
            {identificationDocIssuingAuthority}{' '}
            {identificationDocIssuingDate ? `, ${getClientDateFormat(identificationDocIssuingDate)}` : ''}
          </div>
        </div>
      )}

      {signingIdentificator && (
        <div className="flex gap-2 items-center text-primaryLight">
          <PassportIcon className="w-[28px] h-[28px] shrink-0" />
          <div className="text-primaryLight text-lg font-tektur">{signingIdentificator || '-'}</div>
        </div>
      )}

      {residenceRegistration && (
        <div className="flex gap-2 items-center text-primaryLight">
          <PassportIcon className="w-[28px] h-[28px] shrink-0" />
          <div className="text-primaryLight text-lg font-tektur">{residenceRegistration || '-'}</div>
        </div>
      )}

      <div className="text-sm text-grey">
        *{t('If you want to edit information that is not available to you - contact an administrator')}
      </div>

      {isManagement && (
        <Button onClick={openIdDetailsDrawer} theme="dark">
          {t('Verify data')}
        </Button>
      )}

      {loggedUserId === userId && <Button onClick={openNewPasswordModal}>{t('Reset Password')}</Button>}

      <NewPasswordModal userId={userId} isOpened={newPasswordModalOpened} onClose={closeNewPasswordModal} />

      <UploadDocumentsModal isOpen={isUploadModalOpen} onClose={closeUploadModal} accountType={accountType} userId={userId} />

      <IdDetailsDrawer
        accountType={accountType}
        userId={userId}
        isOpen={isIdDetailsDrawerOpen}
        dateOfBirth={dateOfBirth}
        identificationDocIssuingAuthority={identificationDocIssuingAuthority}
        identificationDocIssuingDate={identificationDocIssuingDate}
        residenceRegistration={residenceRegistration}
        signingIdentificator={signingIdentificator}
        email={email}
        phone={phone}
        firstName={firstName}
        lastName={lastName}
        company={company}
        middleName={middleName}
        identificationNumber={identificationNumber}
        onClose={closeIdDetailsDrawer}
      />
    </div>
  );
});
